import React, { useEffect, useState } from 'react';
import { Box, FormControl, InputLabel } from '@mui/material';

import { apiRequest } from '@/src/services';
import { getZeroOption, type Option, getValidSelectValue, Constants } from '@/src/utils';
import {
    type InitialOptions,
    type ClassOption,
    type SchoolClassOptions,
    type ExamOption,
    type FilterState,
} from '@/src/pages/reports/comparisonSummary/models/comparisonSummaryFilterDTOs';
import {
    ErrorMessage,
    FilterIcon,
    ICON_COLORS,
    SimpleButton,
    SimpleMultipleSelect,
    SimpleSingleSelect,
    SimpleRadio,
    FilterBarContainer,
} from '@/src/ui';
import { useAuth } from '@/src/app/context/useAuth';

interface FiltersProps {
    refreshTableData: (filters: FilterState) => void;
    filterState: FilterState;
    setFilterState: React.Dispatch<React.SetStateAction<FilterState>>;
}

const ViewModeVisibility: Record<string, number[]> = {
    'Super Admin': [1, 2, 3],
    'District Admin': [1, 2, 3],
    'School Admin': [2, 3],
    Teacher: [3],
};

const AllOption = getZeroOption('All');
const LoadingOption = getZeroOption('Loading...');
const DefaultDistrictOption = getZeroOption('Select District');
const DefaultSchoolOption = getZeroOption('All');
const DefaultGradeOption = getZeroOption('Select Grade');
const NoClassOption = getZeroOption('No Classes Available');
const NoExamOption = getZeroOption('No Assessments Available');

const ViewModeOptions: Option[] = [
    { id: 1, name: 'District' },
    { id: 2, name: 'School' },
    { id: 3, name: 'Class' },
];

const requiredFields = ['districtId', 'gradeLevelMasterId', 'examIds'] as const;
function getMissingFields(fs: any): string[] {
    return requiredFields
        .filter((key) => {
            if (key === 'examIds') return !fs.examIds || !fs.examIds.length;
            return !fs[key] || fs[key] === 0 || fs[key] === Number(AllOption.id);
        })
        .map((key) => Constants.fieldLabels[key] || key);
}

const Filters: React.FC<FiltersProps> = ({ refreshTableData, filterState, setFilterState }) => {
    const { user } = useAuth();
    const userRole = user?.role;

    const [error, setError] = useState('');

    const [districtOptions, setDistrictOptions] = useState<Option[]>([LoadingOption]);
    const [schoolOptions, setSchoolOptions] = useState<Option[]>([DefaultSchoolOption]);
    const [classOptions, setClassOptions] = useState<Option[]>([AllOption]);
    const [gradeLevelMasterOptions, setGradeLevelMasterOptions] = useState<Option[]>([
        LoadingOption,
    ]);
    const [filtersLoading, setFiltersLoading] = useState(true);
    const [examTypeMasterOptions, setExamTypeMasterOptions] = useState<Option[]>([AllOption]);
    const [examDataAll, setExamDataAll] = useState<ExamOption[] | null>(null);
    const [examOptions, setExamOptions] = useState<Option[]>([AllOption]);
    const [examsLoading, setExamsLoading] = useState(false);
    const [viewModeOptions, setViewModeOptions] = useState<Option[]>([]);
    const [classData, setClassData] = useState<ClassOption[]>([]);
    const [fieldsChanged, setFieldsChanged] = useState(false);

    // --- Fetch initial options ---
    useEffect(() => {
        (async function init() {
            try {
                const res = await apiRequest<InitialOptions>(
                    'GET',
                    'utility/filterData?getGradeLevelMaster=true&getDistrict=true&getExamTypeMaster=true'
                );
                const data = res.data;

                setGradeLevelMasterOptions([DefaultGradeOption, ...data.gradeLevelMasterOptions]);
                const sortedDistricts = data.districtOptions.sort((a, b) =>
                    a.name.localeCompare(b.name)
                );
                setDistrictOptions([DefaultDistrictOption, ...sortedDistricts]);
                setExamTypeMasterOptions([
                    AllOption,
                    ...data.examTypeMasterOptions.sort((a, b) => a.name.localeCompare(b.name)),
                ]);
                setFilterState((prev) => {
                    const next = {
                        ...prev,
                        gradeLevelMasterId: prev.gradeLevelMasterId || 0,
                        districtId: prev.districtId || 0,
                        schoolId: prev.schoolId || Number(DefaultSchoolOption.id),
                        classId: prev.classId || Number(AllOption.id),
                        examTypeMasterId: prev.examTypeMasterId || Number(AllOption.id),
                        examIds: prev.examIds && prev.examIds.length ? prev.examIds : [],
                        viewMode: prev.viewMode || 1,
                    };
                    if (next.districtId && next.districtId !== 0) {
                        setTimeout(async () => {
                            await fetchSchoolClassOptions(
                                next.districtId,
                                next.schoolId,
                                next.classId
                            );
                            setFiltersLoading(false);
                        }, 0);
                    } else {
                        setFiltersLoading(false);
                    }
                    return next;
                });
                setError('');
            } catch {
                setDistrictOptions([AllOption]);
                setGradeLevelMasterOptions([DefaultGradeOption]);
                setExamTypeMasterOptions([AllOption]);
                setFiltersLoading(false);
                setError(
                    'Failed to fetch initial filter options. Please refresh the page or try again.'
                );
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [setFilterState]);

    const fetchSchoolClassOptions = async (
        districtId: number | string,
        savedSchoolId?: number,
        savedClassId?: number
    ) => {
        if (!districtId) {
            setSchoolOptions([DefaultSchoolOption]);
            setClassOptions([AllOption]);
            setFilterState((prev) => ({
                ...prev,
                schoolId: Number(DefaultSchoolOption.id),
                classId: Number(AllOption.id),
            }));
            return;
        }

        try {
            const res = await apiRequest<SchoolClassOptions>(
                'GET',
                `utility/filterData?getSchool=true&getClass=true&districtId=${districtId}`
            );
            const data = res.data;

            const sortedSchools = data.schoolOptions.sort((a, b) => a.name.localeCompare(b.name));
            setSchoolOptions([DefaultSchoolOption, ...sortedSchools]);
            const validSchoolId =
                savedSchoolId && sortedSchools.some((opt) => opt.id === savedSchoolId)
                    ? savedSchoolId
                    : Number(AllOption.id);
            setFilterState((prev) => ({ ...prev, schoolId: Number(validSchoolId) }));

            const sortedClassData = data.classOptions.sort((a, b) => a.name.localeCompare(b.name));
            setClassData(sortedClassData);
            let filteredClassOptions = sortedClassData;
            if (
                validSchoolId &&
                validSchoolId !== Number(AllOption.id) &&
                validSchoolId !== Number(DefaultSchoolOption.id)
            ) {
                filteredClassOptions = sortedClassData.filter(
                    (opt) => opt.schoolId === Number(validSchoolId)
                );
            }
            const gradeId = filterState.gradeLevelMasterId;
            if (gradeId && gradeId !== 0) {
                filteredClassOptions = filteredClassOptions.filter(
                    (opt) =>
                        Array.isArray(opt.gradeLevelMasterIds) &&
                        opt.gradeLevelMasterIds.includes(gradeId)
                );
            }
            filteredClassOptions = filteredClassOptions.sort((a, b) =>
                a.name.localeCompare(b.name)
            );
            const classOptionsArr = filteredClassOptions.length
                ? [
                      AllOption,
                      ...filteredClassOptions.map((opt) => ({ id: opt.id, name: opt.name })),
                  ]
                : [NoClassOption];
            setClassOptions(classOptionsArr);

            const validClassId =
                savedClassId && filteredClassOptions.some((opt) => opt.id === savedClassId)
                    ? savedClassId
                    : Number(AllOption.id);
            setFilterState((prev) => ({ ...prev, classId: Number(validClassId) }));
            setError('');
        } catch {
            setError(
                'Failed to fetch school and class options. Please refresh the page or try again.'
            );
            setSchoolOptions([DefaultSchoolOption]);
            setClassOptions([AllOption]);
        }
    };
    const getLoadingProps = (opts: Option[], val: number, loading: boolean) =>
        loading
            ? { options: [LoadingOption], value: 0, disabled: true }
            : { options: opts, value: val };

    // --- ViewMode options ---
    useEffect(() => {
        if (!userRole) return;
        const allowedModes = ViewModeVisibility[userRole] || [];
        const filteredOptions = ViewModeOptions.filter((opt) =>
            allowedModes.includes(Number(opt.id))
        );
        setViewModeOptions(filteredOptions);

        setFilterState((prev) => {
            if (!allowedModes.includes(prev.viewMode)) {
                return { ...prev, viewMode: Number(filteredOptions[0]?.id ?? 1) };
            }
            return prev;
        });
    }, [userRole, setFilterState]);

    // --- Exam Pipeline ---
    const maybeFetchExamOptions = async (gradeLevelMasterId: number) => {
        if (!gradeLevelMasterId || gradeLevelMasterId === Number(DefaultGradeOption.id)) {
            setExamDataAll(null);
            setExamsLoading(false);
            return;
        }

        setExamsLoading(true);

        try {
            const res = await apiRequest<ExamOption[]>(
                'GET',
                `utility/filterData/assignedExams?DistrictId=${filterState.districtId}&SchoolId=${filterState.schoolId}&GradeLevelMasterId=${gradeLevelMasterId}&ClassId=${filterState.classId ?? 0}`
            );
            const data = (res.data ?? [])
                .filter((exam) => exam.schoolIds?.length)
                .sort((a, b) => a.name.localeCompare(b.name));

            setExamDataAll(data);
        } catch {
            setExamDataAll([]);
        } finally {
            setExamsLoading(false);
        }
    };

    const filterExamsLocally = (
        exams: ExamOption[] | null,
        schoolId: number,
        classId: number,
        examTypeMasterId: number
    ) => {
        if (!exams) return;

        if (exams.length === 0) {
            setExamOptions([NoExamOption]);
            setFilterState((prev) => ({ ...prev, examIds: [] }));
            return;
        }

        const filtered = exams.filter((exam) => {
            if (!exam.schoolIds?.length) return false;
            if (schoolId && schoolId !== Number(AllOption.id) && !exam.schoolIds.includes(schoolId))
                return false;
            if (
                classId &&
                classId !== Number(AllOption.id) &&
                exam.classIds?.length &&
                !exam.classIds.includes(classId)
            )
                return false;
            if (
                examTypeMasterId &&
                examTypeMasterId !== Number(AllOption.id) &&
                exam.examTypeMasterId !== examTypeMasterId
            )
                return false;
            return true;
        });

        if (!filtered.length) {
            setExamOptions([NoExamOption]);
            setFilterState((prev) => ({ ...prev, examIds: [] }));
        } else {
            setExamOptions(filtered);
            setFilterState((prev) => ({
                ...prev,
                examIds: prev.examIds.filter((id) => filtered.some((e) => e.id === id)),
            }));
        }
    };

    useEffect(() => {
        maybeFetchExamOptions(filterState.gradeLevelMasterId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filterState.gradeLevelMasterId, filterState.classId]);

    useEffect(() => {
        filterExamsLocally(
            examDataAll,
            filterState.schoolId,
            filterState.classId,
            filterState.examTypeMasterId
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [examDataAll, filterState.schoolId, filterState.classId, filterState.examTypeMasterId]);

    // --- Update class options ---
    const updateClassOptions = (
        data: ClassOption[],
        gradeLevelMasterId: number,
        schoolId: number
    ) => {
        if (schoolId === Number(AllOption.id)) {
            const newClassOptions = data
                .filter((option) => {
                    const glids = option.gradeLevelMasterIds;
                    return !glids?.length || glids.includes(Number(gradeLevelMasterId));
                })
                .map((option) => ({ id: option.id, name: option.name }));
            setClassOptions([AllOption, ...newClassOptions]);
            setFilterState((prev) => ({ ...prev, classId: Number(AllOption.id) }));
            return;
        }

        const newClassOptions = data
            .filter((option) => {
                if (schoolId && option.schoolId !== Number(schoolId)) return false;
                const glids = option.gradeLevelMasterIds;
                return !glids?.length || glids.includes(Number(gradeLevelMasterId));
            })
            .map((option) => ({ id: option.id, name: option.name }));

        if (!newClassOptions.length) {
            setClassOptions([NoClassOption]);
            setFilterState((prev) => ({ ...prev, classId: Number(NoClassOption.id) }));
            return;
        }

        setClassOptions([AllOption, ...newClassOptions]);
        setFilterState((prev) => ({ ...prev, classId: Number(AllOption.id) }));
    };

    const isSelectAllDisabled =
        filterState.districtId === Number(AllOption.id) ||
        filterState.gradeLevelMasterId === Number(AllOption.id) ||
        examsLoading ||
        filtersLoading ||
        examOptions.length === 0 ||
        (examOptions.length === 1 && examOptions[0].id === Number(NoExamOption.id));

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 2 }}>
            <FilterBarContainer>
                <SimpleSingleSelect
                    name="district-select"
                    label="District *"
                    {...getLoadingProps(
                        districtOptions,
                        Number(
                            getValidSelectValue(
                                filterState.districtId,
                                districtOptions,
                                districtOptions[0]?.id ?? 0
                            )
                        ),
                        filtersLoading
                    )}
                    onChange={(e) => {
                        setFieldsChanged(true);
                        const newDistrictId = Number(e.target.value);
                        const newFIlterState = {
                            ...filterState,
                            districtId: newDistrictId,
                            schoolId: Number(DefaultSchoolOption.id),
                            classId: Number(AllOption.id),
                        };
                        setFilterState(newFIlterState);
                        fetchSchoolClassOptions(newDistrictId);
                    }}
                    sx={{ minWidth: 140, maxWidth: 280 }}
                />
                <SimpleSingleSelect
                    name="school-select"
                    label="School"
                    {...getLoadingProps(
                        schoolOptions,
                        Number(
                            getValidSelectValue(
                                filterState.schoolId,
                                schoolOptions,
                                schoolOptions[0]?.id ?? 0
                            )
                        ),
                        filtersLoading
                    )}
                    onChange={(e) => {
                        setFieldsChanged(true);
                        const newSchoolId = Number(e.target.value);
                        const newFilterState = {
                            ...filterState,
                            schoolId: newSchoolId,
                            classId: Number(AllOption.id),
                        };
                        setFilterState(newFilterState);
                        updateClassOptions(classData, filterState.gradeLevelMasterId, newSchoolId);
                    }}
                    sx={{ minWidth: 220, maxWidth: 280 }}
                    disabled={
                        filtersLoading || !filterState.districtId || filterState.districtId === 0
                    }
                />
                <SimpleSingleSelect
                    name="grade-level-select"
                    label="Grade *"
                    {...getLoadingProps(
                        gradeLevelMasterOptions,
                        Number(
                            getValidSelectValue(
                                filterState.gradeLevelMasterId,
                                gradeLevelMasterOptions,
                                gradeLevelMasterOptions[0]?.id ?? 0
                            )
                        ),
                        filtersLoading
                    )}
                    onChange={(e) => {
                        setFieldsChanged(true);
                        const newGrade = Number(e.target.value);
                        const newFilterState = {
                            ...filterState,
                            gradeLevelMasterId: newGrade,
                            classId: Number(AllOption.id),
                        };
                        setFilterState(newFilterState);
                        updateClassOptions(classData, newGrade, filterState.schoolId);
                    }}
                    sx={{ minWidth: 140, maxWidth: 280 }}
                    disabled={filtersLoading}
                />
                <SimpleSingleSelect
                    name="class-select"
                    label="Class"
                    {...getLoadingProps(
                        classOptions,
                        Number(
                            getValidSelectValue(
                                filterState.classId,
                                classOptions,
                                classOptions[0]?.id ?? 0
                            )
                        ),
                        filtersLoading
                    )}
                    onChange={(e) => {
                        setFieldsChanged(true);
                        const newClassId = Number(e.target.value);
                        setFilterState((prev) => ({
                            ...prev,
                            classId: newClassId,
                        }));
                    }}
                    sx={{ minWidth: 220, maxWidth: 280 }}
                    disabled={
                        filtersLoading ||
                        !filterState.gradeLevelMasterId ||
                        filterState.gradeLevelMasterId === 0
                    }
                />
            </FilterBarContainer>
            <FilterBarContainer>
                <FormControl size="small" sx={{ minWidth: 120, maxWidth: 380, mb: 0.5, mt: '4px' }}>
                    <InputLabel
                        shrink
                        htmlFor="view-mode-radio"
                        sx={{ background: 'white', px: 0.5 }}
                    >
                        View Mode
                    </InputLabel>
                    <Box
                        id="view-mode-radio"
                        sx={{
                            display: 'flex',
                            flexDirection: 'row',
                            alignItems: 'center',
                            gap: 1.5,
                            border: (theme) => `1px solid ${theme.palette.grey[400]}`,
                            borderRadius: 1,
                            backgroundColor: (theme) => theme.palette.background.paper,
                            px: 2,
                            py: 1.5,
                            maxHeight: 40,
                        }}
                    >
                        {viewModeOptions.map((option) => (
                            <SimpleRadio
                                key={option.id}
                                name="view-mode"
                                label={option.name}
                                value={String(option.id)}
                                checked={filterState.viewMode === Number(option.id)}
                                onChange={() => {
                                    setFieldsChanged(true);
                                    setFilterState((prev) => ({
                                        ...prev,
                                        viewMode: Number(option.id),
                                    }));
                                }}
                                sx={{ marginRight: 0 }}
                            />
                        ))}
                    </Box>
                </FormControl>
                <SimpleSingleSelect
                    name="exam-type-select"
                    label="Type"
                    value={filterState.examTypeMasterId}
                    onChange={(e) => {
                        setFieldsChanged(true);
                        const newExamTypeMasterId = Number(e.target.value);
                        setFilterState((prev) => ({
                            ...prev,
                            examTypeMasterId: newExamTypeMasterId,
                        }));
                    }}
                    options={examTypeMasterOptions}
                    sx={{ minWidth: 140, maxWidth: 280 }}
                    disabled={filtersLoading}
                />

                <SimpleMultipleSelect
                    name="exam-select"
                    label="Assessments *"
                    value={examsLoading || filtersLoading ? [] : filterState.examIds}
                    options={
                        examsLoading || filtersLoading
                            ? [LoadingOption]
                            : examOptions.length
                              ? examOptions
                              : [NoExamOption]
                    }
                    onChange={(newSelectedIds) => {
                        setFieldsChanged(true);
                        if (newSelectedIds.includes(Number(NoExamOption.id))) return;
                        setFilterState((prev) => ({
                            ...prev,
                            examIds: newSelectedIds.filter((id) => id !== Number(AllOption.id)),
                        }));
                    }}
                    disabled={
                        examsLoading ||
                        filtersLoading ||
                        examOptions.length === 0 ||
                        examOptions[0]?.id === NoExamOption.id
                    }
                    sx={{ minWidth: 220, maxWidth: 280 }}
                />
                <SimpleButton
                    variant="outlined"
                    sx={{ minHeight: 40, height: 40, alignSelf: 'center' }}
                    onClick={() => {
                        const allId = Number(AllOption.id);
                        const noExamId = Number(NoExamOption.id);
                        const availableExamIds = examOptions
                            .filter((opt) => opt.id !== allId && opt.id !== noExamId)
                            .map((opt) => Number(opt.id));
                        const allSelected = availableExamIds.every((id) =>
                            filterState.examIds.includes(id)
                        );
                        setFieldsChanged(true);
                        setFilterState((prev) => ({
                            ...prev,
                            examIds: allSelected ? [] : availableExamIds,
                        }));
                    }}
                    disabled={isSelectAllDisabled}
                >
                    {(() => {
                        const allId = Number(AllOption.id);
                        const noExamId = Number(NoExamOption.id);
                        const availableExamIds = examOptions
                            .filter((opt) => opt.id !== allId && opt.id !== noExamId)
                            .map((opt) => Number(opt.id));
                        const allSelected = availableExamIds.every((id) =>
                            filterState.examIds.includes(id)
                        );
                        return allSelected ? 'Deselect All Assessments' : 'Select All Assessments';
                    })()}
                </SimpleButton>
                <SimpleButton
                    onClick={() => {
                        const missing = getMissingFields(filterState);
                        if (missing.length) {
                            setError(
                                `Please select the following required fields before filtering: ${missing.join(', ')}.`
                            );
                            return;
                        }
                        setError('');
                        setFieldsChanged(false);
                        setFilterState((prev) => ({ ...prev, useMock: false }));
                        refreshTableData(filterState);
                    }}
                    variant={fieldsChanged ? 'outlined' : 'contained'}
                    color="primary"
                    sx={{ minHeight: 40, height: 40, alignSelf: 'center', padding: '8px 25px' }}
                >
                    <FilterIcon
                        color={fieldsChanged ? ICON_COLORS.primary : ICON_COLORS.secondary}
                        style={{ width: '16px', height: '16px', marginRight: '8px' }}
                    />{' '}
                    Filter
                </SimpleButton>
            </FilterBarContainer>
            <ErrorMessage error={error} style={{ textAlign: 'center', marginBottom: '8px' }} />
        </Box>
    );
};

export default Filters;
